"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../lib/utils";
import Icon from "./icon";

const CommandPopover = DialogPrimitive.Root;

const CommandPopoverTrigger = DialogPrimitive.Trigger;

const CommandPopoverPortal = DialogPrimitive.Portal;

const CommandPopoverClose = DialogPrimitive.Close;

type CommandPopoverOverlayProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
> & { ref?: React.Ref<HTMLDivElement> };
const CommandPopoverOverlay = ({
  className,
  ref,
  ...props
}: CommandPopoverOverlayProps) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
);
CommandPopoverOverlay.displayName = DialogPrimitive.Overlay.displayName;

type CommandPopoverContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & { ref?: React.Ref<HTMLDivElement> };
const CommandPopoverContent = ({
  className,
  children,
  ref,
  ...props
}: CommandPopoverContentProps) => (
  <CommandPopoverPortal>
    <CommandPopoverOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <Icon icon="x" className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </CommandPopoverPortal>
);
CommandPopoverContent.displayName = DialogPrimitive.Content.displayName;

const CommandPopoverHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
CommandPopoverHeader.displayName = "CommandPopoverHeader";

const CommandPopoverFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
CommandPopoverFooter.displayName = "CommandPopoverFooter";

type CommandPopoverTitleProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Title
> & { ref?: React.Ref<HTMLHeadingElement> };
const CommandPopoverTitle = ({
  className,
  ref,
  ...props
}: CommandPopoverTitleProps) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
);
CommandPopoverTitle.displayName = DialogPrimitive.Title.displayName;

type CommandPopoverDescriptionProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
> & { ref?: React.Ref<HTMLParagraphElement> };
const CommandPopoverDescription = ({
  className,
  ref,
  ...props
}: CommandPopoverDescriptionProps) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);
CommandPopoverDescription.displayName = DialogPrimitive.Description.displayName;

export {
  CommandPopover,
  CommandPopoverPortal,
  CommandPopoverOverlay,
  CommandPopoverTrigger,
  CommandPopoverClose,
  CommandPopoverContent,
  CommandPopoverHeader,
  CommandPopoverFooter,
  CommandPopoverTitle,
  CommandPopoverDescription,
};
